<script setup lang="ts">
import { useFormValidation } from '../../../../../composables/useFormValidation'
import { required } from '../../../../../utils/validation'
import TextInput from '../../../../../components/ui/form/TextInput.vue'
import NumberInput from '../../../../../components/ui/form/NumberInput.vue'
import CheckInput from '../../../../../components/ui/form/CheckInput.vue'
import { api } from '../../../../../api/Api'
import Toast from '../../../../../services/Toast'
import { Permission } from '../../../../../types/auth'
import { ref, onMounted } from 'vue'
import ModalFormTemplate from '../../../../../components/ui/Templates/ModalFormTemplate.vue'
import SearchSelect from '../../../../../components/ui/form/SearchSelect.vue'

const props = defineProps<{
  data?: any | null
  mode: 'create' | 'edit' | 'view' | 'showQuickView' | 'toggleStatus'
  title: string | null
  endPoint: string,
  permissions?: Permission[],
  item: any,
  listCreateName?: string
}>()

const emit = defineEmits<{
  (e: 'save', data: any): void
  (e: 'close'): void
}>()

const { fields, formData, setFieldRules, validateForm, resetForm, setFormData } = useFormValidation({
    active: props.data?.active || false,
    always_charge_delivery: props.data?.always_charge_delivery || false,
    always_charge_packing: props.data?.always_charge_packing || false,
    update_all_price_lists: props.data?.update_all_price_lists || false,
    update_all_variants: props.data?.update_all_variants || false,
    delivery_charges: props.data?.delivery_charges || 0,
    free_delivery_amount: props.data?.free_delivery_amount || 0,
    inventory_qty: props.data?.inventory_qty || 1,
    item: props.data?.item || '',
    items_id: props.data?.items_id || '',
    item_variants_id: props.data?.item_variants_id || '',
    packing_price: props.data?.packing_price || 0,
    points: props.data?.points || 0,
    price_list: props.data?.price_list || '',
    price_lists_id: props.data?.price_lists_id || '',
    selling_price: props.data?.selling_price || '',
    uid: props.data?.uid || '',
})

setFieldRules('items_id', [required('Item is required')])
setFieldRules('price_lists_id', [required('Price List is required')])
setFieldRules('selling_price', [required('Selling is required')])
setFieldRules("item_variants_id", props.item?.has_variants && !fields.value.update_all_variants.value ? [required("Item Variant is required")] : []);


const handleSubmit = async () => {
  if (fields.value.update_all_variants.value) {
    fields.value.item_variants_id.value = "";
    setFieldRules("item_variants_id", []);
  } else {
    setFieldRules("item_variants_id", [required("Item Variant is required")]);
  }
  console.log("handleSubmit", formData.value);
  if (validateForm()) {
    const response = await api.post("/clients/inventory/item-management/items/update-prices", formData.value)
    if (response.success == true) {
      Toast.show('saved successfully')
      emit('save', response.data)
      emit('close')
    }
  }
}

const toggleAllVariants = () => {
  console.log(fields.value.update_all_variants.value);
  if (fields.value.update_all_variants.value) {
    fields.value.item_variants_id.value = "";
    setFieldRules("item_variants_id", []);
  } else {
    setFieldRules("item_variants_id", [required("Item Variant is required")]);
  }
};

const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == 'view'
  if (props.mode == 'create') {
      resetForm()
      setFormData({items_id: props.item?.id, item: props.item?.name})
    } else if (props.data) {
        setFormData(props.data)
    }
})

/*const handleCustomAction = async (action: string, row: any) => {
  console.log(action, row)
}*/
</script>
<template>
  <ModalFormTemplate :data="data" :mode="mode" :title="title" :endPoint="endPoint" :perm="permissions"
    :listCreateName="listCreateName" :tabList="[]" :formData="formData" @save="handleSubmit" 
    :showSave="false" :showEdit="false" :showSaveNew="false"
    @close="$emit('close')">
    <template #form>
      <div class="grid md:grid-cols-4 gap-6">

        <TextInput v-model="fields.item.value" class="col-span-2" label="Item" placeholder="Enter Name" :readonly="true"
          :error="fields.items_id.touched ? fields.items_id.error : null" @blur="fields.items_id.touched = true" />

          <SearchSelect v-model="fields.item_variants_id.value" label="Item Variant" :readonly="isReadOnly"
          :error="fields.item_variants_id.touched ? fields.item_variants_id.error : null" @blur="fields.item_variants_id.touched = true"
          :options="item.item_variants" />

          <CheckInput v-model="fields.update_all_variants.value" :readonly="isReadOnly" label="Update All Variants"
        @change="toggleAllVariants"
        :error="fields.update_all_variants.touched ? fields.update_all_variants.error : null" @blur="fields.update_all_variants.touched = true" />

          <CheckInput v-model="fields.update_all_price_lists.value" :readonly="isReadOnly" label="Update All Price Lists"          
            :error="fields.update_all_price_lists.touched ? fields.update_all_price_lists.error : null" @blur="fields.update_all_price_lists.touched = true" />        

        <SearchSelect v-model="fields.price_lists_id.value" label="Price List" :readonly="isReadOnly"
          :error="fields.price_lists_id.touched ? fields.price_lists_id.error : null" @blur="fields.price_lists_id.touched = true"
          end-point="finance/master-data/price-lists" />
          

        <NumberInput v-model="fields.selling_price.value" label="Selling Price" :readonly="isReadOnly"
          :error="fields.selling_price.touched ? fields.selling_price.error : null" @blur="fields.selling_price.touched = true" />

          <CheckInput v-model="fields.always_charge_delivery.value" :readonly="isReadOnly" label="Always Charge Delivery"
            :error="fields.active.touched ? fields.active.error : null" @blur="fields.active.touched = true" />
        <CheckInput v-model="fields.always_charge_packing.value" :readonly="isReadOnly" label="Always Charge Packing"
            :error="fields.active.touched ? fields.active.error : null" @blur="fields.active.touched = true" />        
        <NumberInput v-model="fields.delivery_charges.value" label="Delivery Charge" :readonly="isReadOnly"
            :error="fields.selling_price.touched ? fields.selling_price.error : null" @blur="fields.selling_price.touched = true" />
        <NumberInput v-model="fields.free_delivery_amount.value" label="Free Delivery Amount" :readonly="isReadOnly"
            :error="fields.selling_price.touched ? fields.selling_price.error : null" @blur="fields.selling_price.touched = true" />
        <NumberInput v-model="fields.packing_price.value" label="Packing Price" :readonly="isReadOnly"
            :error="fields.selling_price.touched ? fields.selling_price.error : null" @blur="fields.selling_price.touched = true" />
        <NumberInput v-model="fields.points.value" label="Points" :readonly="isReadOnly"
            :error="fields.selling_price.touched ? fields.selling_price.error : null" @blur="fields.selling_price.touched = true" />

        <CheckInput v-model="fields.active.value" :readonly="isReadOnly" label="Active"
          :error="fields.active.touched ? fields.active.error : null" @blur="fields.active.touched = true" />                  

      </div>
    </template>
  </ModalFormTemplate>
</template>